import { useAffiliateFirm } from '@commenda/api';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Button, Icons, Loading, useToggle } from '@commenda/kakashi';
import { UserEntityLinkRole } from '@commenda/prisma';
import { AddClientModal } from 'components/affiliate-firms/AddClientModal';
import { AffiliateClientsTable } from 'components/affiliate-firms/AffiliateClientsTable';
import { ManageAffiliateAssigneesModal } from 'components/affiliate-firms/ManageAffiliatesAssigneesModal';
import { AffiliateConsolePage } from 'components/Layouts/AffiliateConsolePage';
import { CommendaSidebar } from 'components/Layouts/Main/Sidebar';
import { useConfig } from 'context/useConfig';
import { useState } from 'react';

const HomeScreenForAffiliates = () => {
  const { affiliateFirmId, user } = useConfig();

  // The redesign is demo work on a live product surface, so the page it replaces has to stay reachable.
  const isAmTpDemoOn = isFeatureLaunched(FeatureLaunch.AM_TP_DEMO);

  const {
    data: affiliateFirmResponse,
    error: affiliateFirmError,
    mutate: refreshAffiliateFirm,
  } = useAffiliateFirm(affiliateFirmId);

  const addClientModalVisibility = useToggle();
  const manageAffiliateAssigneesModalVisibility = useToggle();

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );

  if (!affiliateFirmResponse) {
    if (affiliateFirmError) console.error(affiliateFirmError);
    return (
      <CommendaSidebar>
        <Loading />
      </CommendaSidebar>
    );
  }

  const roleInAffiliateFirm = affiliateFirmResponse.affiliateFirm.users.find(
    (usr) => usr.id === user?.id
  )?.role;

  const shouldShowAddClientButton =
    roleInAffiliateFirm === UserEntityLinkRole.AFFILIATE_ADMIN;

  const clientsTable = (
    <AffiliateClientsTable
      onAddClient={addClientModalVisibility.show}
      onManageAssignees={(clientId) => {
        setSelectedCompanyId(+clientId);
        manageAffiliateAssigneesModalVisibility.show();
      }}
      affiliateFirm={affiliateFirmResponse.affiliateFirm}
    />
  );

  return (
    <CommendaSidebar>
      <AddClientModal
        isOpen={addClientModalVisibility.visible}
        onClose={addClientModalVisibility.hide}
        affiliateFirmId={affiliateFirmId}
      />
      <ManageAffiliateAssigneesModal
        isOpen={manageAffiliateAssigneesModalVisibility.visible}
        onClose={manageAffiliateAssigneesModalVisibility.hide}
        affiliateFirm={affiliateFirmResponse.affiliateFirm}
        selectedCompanyId={selectedCompanyId}
        onChanges={async () => {
          await refreshAffiliateFirm();
        }}
      />
      {isAmTpDemoOn ? (
        <AffiliateConsolePage>
          <header className="flex items-end justify-between border-b border-border-secondary p-3xl">
            <h1 className="display-xs-bold py-xs text-text-primary">Clients</h1>
            {shouldShowAddClientButton && (
              <Button size="md" onClick={addClientModalVisibility.show}>
                <Icons.Plus className="size-4" />
                <span>Add client</span>
              </Button>
            )}
          </header>
          <section className="flex min-h-0 flex-1 flex-col overflow-y-auto p-3xl">
            {clientsTable}
          </section>
        </AffiliateConsolePage>
      ) : (
        <main className="flex h-screen flex-col">
          <div className="h-10 w-full bg-bg-primary border-b border-border-primary py-md px-xl">
            <div className="size-6 flex items-center justify-center">
              <Icons.Building06 className="size-4 text-fg-tertiary" />
            </div>
          </div>
          <section className="flex flex-1 flex-col overflow-y-auto px-6xl gap-4xl py-5xl">
            <div className="flex items-center justify-between">
              <h3 className="display-xs-bold">Clients</h3>
              {shouldShowAddClientButton && (
                <Button size="sm" onClick={addClientModalVisibility.show}>
                  <Icons.Plus className="size-4" />
                  <span>Add client</span>
                </Button>
              )}
            </div>
            {clientsTable}
          </section>
        </main>
      )}
    </CommendaSidebar>
  );
};

export default HomeScreenForAffiliates;
